import { EditorPreset } from './types';

/**
 * One toolbar for every authoring surface.
 *
 * The presets used to differ per surface, which meant the narrower ones
 * silently drifted from `full` — headings, indent, blockquote, code-block
 * and the checklist disappeared from question bodies as a side effect of
 * the split rather than a decision. Authors now get the identical toolbar
 * everywhere, so there is nothing to drift.
 *
 * `gap` is the ONE exception, and only for the formula button: Quill's
 * formula blot stores LaTeX containing literal [ and ] characters, and the
 * backend's gap parser reads brackets as gap markers, so a formula
 * corrupts a fill-in-the-gap question. Everything else is shared.
 *
 * The preset names survive because ~42 call sites pass them; they now all
 * resolve to the same array except `gap`.
 *
 * Inline `code` is deliberately absent — `code-block` covers the need and
 * the inline variant was not wanted. The header dropdown is absent too:
 * it duplicated `size`, which is preferred here because it writes an
 * inline font-size rather than an h1-h6 block that overwhelms a question
 * card. Existing headings still render (see editorFormats).
 *
 * The video button inserts an embed-URL iframe (Quill's built-in video
 * format), not anything from the platform's video-unit pipeline. Share
 * links from common hosts are normalised to their embeddable form (see
 * registerVideoEmbedFormat); the host must be on the student backend's
 * ContentSanitizer iframe allowlist or students will not see the embed.
 */

/**
 * Size values are CSS lengths, not Quill's small/large/huge keywords,
 * because size registers as an inline-STYLE attributor (see
 * registerInlineStyleFormats) so it renders on the student portal, which
 * ships no Quill stylesheet. Quill's own picker labels only cover the
 * keyword values, so RichTextEditor relabels these three.
 */
const SIZE_VALUES = ['10px', false, '18px', '32px'];

const UNIFIED_TOOLBAR: unknown[][] = [
	[{ font: [] }, { size: SIZE_VALUES }],
	['bold', 'italic', 'underline', 'strike'],
	[{ script: 'sub' }, { script: 'super' }],
	['blockquote', 'code-block'],
	['link', 'image', 'video', 'formula'],
	[
		{ list: 'ordered' },
		{ list: 'bullet' },
		{ list: 'check' },
		{ indent: '-1' },
		{ indent: '+1' },
		{ direction: 'rtl' },
	],
	[{ color: [] }, { background: [] }],
	[{ align: [] }],
	['clean'],
];

/**
 * The unified toolbar minus `formula`. DERIVED, not copied: a hand-kept
 * duplicate is exactly how the presets drifted apart last time.
 */
const GAP_TOOLBAR: unknown[][] = UNIFIED_TOOLBAR.map((group) =>
	group.filter((control) => control !== 'formula')
).filter((group) => group.length > 0);

export const toolbarPresets: Record<EditorPreset, unknown[]> = {
	full: UNIFIED_TOOLBAR,
	question: UNIFIED_TOOLBAR,
	'question-basic': UNIFIED_TOOLBAR,
	gap: GAP_TOOLBAR,
	feedback: UNIFIED_TOOLBAR,
};

/** Kept for backwards compatibility with previous imports. */
export const toolbarOptions = UNIFIED_TOOLBAR;

/**
 * Shared by every preset: a preset controls which BUTTONS are offered,
 * never what content may exist. A format missing from this list is
 * stripped on load even if a button for it is present.
 */
export const editorFormats = [
	// No header BUTTON (size covers making text bigger, and renders as an
	// inline style rather than a block-level heading). The format stays
	// whitelisted so headings already saved in older Web Content and
	// Assignment bodies survive a load/save round-trip instead of being
	// silently stripped.
	'header',
	'font',
	'size',
	'bold',
	'italic',
	'underline',
	'strike',
	'script',
	'align',
	'direction',
	'indent',
	'blockquote',
	'code-block',
	'list',
	'link',
	'image',
	'video',
	'formula',
	'color',
	'background',
];
